import { useMemo } from 'react';
import { ItemTile, GroupedItemList } from '@/components/ui';
import { formatDateKey, expandItemsToEntries, type DisplayEntry } from '@/lib/itemListUtils';
import type { NoteItem } from '@perfin/shared';
import type { GroupBy, TabKey } from '@/stores/uiStore';

interface NoteGridProps {
  notes: NoteItem[];
  groupBy?: GroupBy;
  currentTab?: TabKey;
  hideStatusToggle?: boolean;
  referenceDate?: Date;
  statusFilter?: string | null;
}

export function NoteGrid({ notes, groupBy, currentTab, hideStatusToggle, referenceDate, statusFilter }: NoteGridProps) {
  const displayEntries = useMemo(() => {
    return expandItemsToEntries(notes, currentTab, referenceDate || new Date(), statusFilter);
  }, [notes, currentTab, referenceDate, statusFilter]);

  // For date grouping in time-based tabs, group by effective date (occurrenceDate or dueDate)
  const isTimeBased = currentTab === 'pastWeek' || currentTab === 'today' || currentTab === 'nextWeek';

  if (groupBy === 'date' && isTimeBased) {
    // Manual grouping by effective date
    const dateGroups: Record<string, { entries: DisplayEntry<NoteItem>[]; timestamp: number }> = {};

    for (const entry of displayEntries) {
      // Get effective date: occurrenceDate for recurring, dueDate for scheduled
      let effectiveDate: Date | null = null;
      if (entry.occurrenceDate) {
        effectiveDate = entry.occurrenceDate;
      } else {
        const dueDate = (entry.item as unknown as { dueDate?: { toDate: () => Date } }).dueDate;
        if (dueDate?.toDate) {
          effectiveDate = dueDate.toDate();
        }
      }

      const dateKey = effectiveDate ? formatDateKey(effectiveDate) : 'No Date';
      const timestamp = effectiveDate ? effectiveDate.getTime() : 0;

      if (!dateGroups[dateKey]) {
        dateGroups[dateKey] = { entries: [], timestamp };
      }
      dateGroups[dateKey].entries.push(entry);
    }

    // Sort groups chronologically
    const sortedGroups = Object.entries(dateGroups).sort(([, a], [, b]) => a.timestamp - b.timestamp);

    return (
      <>
        {sortedGroups.map(([dateKey, { entries }]) => (
          <div key={dateKey} className="mb-6">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
              {dateKey} ({entries.length})
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {entries.map((entry) => (
                <ItemTile
                  key={entry.key}
                  item={entry.item}
                  linkPath={`/notes/${entry.item.id}`}
                  currentTab={currentTab}
                  occurrenceDate={entry.occurrenceDate}
                  hideStatusToggle={hideStatusToggle}
                />
              ))}
            </div>
          </div>
        ))}
      </>
    );
  }

  if (groupBy) {
    return (
      <GroupedItemList
        items={displayEntries.map(e => e.item)}
        groupBy={groupBy}
        renderGroup={(key, groupNotes) => {
          const entriesForGroup = displayEntries.filter(e => groupNotes.includes(e.item));
          return (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                {key} ({entriesForGroup.length})
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {entriesForGroup.map((entry) => (
                  <ItemTile
                    key={entry.key}
                    item={entry.item}
                    linkPath={`/notes/${entry.item.id}`}
                    currentTab={currentTab}
                    occurrenceDate={entry.occurrenceDate}
                    hideStatusToggle={hideStatusToggle}
                  />
                ))}
              </div>
            </div>
          );
        }}
        renderItem={(note) => {
          const entry = displayEntries.find(e => e.item.id === note.id) || { item: note, key: note.id, occurrenceDate: undefined };
          return (
            <ItemTile
              key={entry.key}
              item={entry.item}
              linkPath={`/notes/${entry.item.id}`}
              currentTab={currentTab}
              occurrenceDate={entry.occurrenceDate}
              hideStatusToggle={hideStatusToggle}
            />
          );
        }}
      />
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {displayEntries.map((entry) => (
        <ItemTile
          key={entry.key}
          item={entry.item}
          linkPath={`/notes/${entry.item.id}`}
          currentTab={currentTab}
          occurrenceDate={entry.occurrenceDate}
          hideStatusToggle={hideStatusToggle}
        />
      ))}
    </div>
  );
}
